import { useLogError } from '@/src/common/hooks/useLogError'
import { graphql } from '@api/mesa/gql'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { sortByTimeDescending } from '@/src/common/utils/date'
import {
  CalculatedTransferAmountType,
  type GetIncomingScheduledTransfersQueryVariables,
  type GetIncomingScheduledTransfersQuery,
} from '@api/mesa/graphql'
import { useMemo } from 'react'
import type { QueryHookOptions } from '@apollo/client'

// not being used because see the comment on `fetchPolicy: 'no-cache'`
// graphql(`
//   fragment TransferAmountFragment on TransferAmountResponse {
//     __typename
//     ... on ManualTransferAmountResponse {
//       renderedAmount {
//         amount
//         currencyCode
//       }
//     }
//     ... on TransferAmountCalculationResponse {
//       calculatedType
//     }
//   }
// `)

graphql(`
  fragment HighnoteExternalAccountFragment on HighnoteExternalAccountResponse {
    highnoteExternalAccountId
    bankAccountLast4
    bankName
    accountName
    accountSubtype
  }
`)

export const GET_INCOMING_SCHEDULED_TRANSFERS = graphql(`
  query GetIncomingScheduledTransfers {
    incomingScheduledTransfers {
      page {
        __typename
        ... on OneTimeAchTransferResponse {
          id
          status
          transferAmount {
            # ...TransferAmountFragment

            __typename
            ... on ManualTransferAmountResponse {
              renderedAmount {
                amount
                currencyCode
              }
            }
            ... on TransferAmountCalculationResponse {
              calculatedType
            }
          }
          scheduledTransferDate
          fromHighnoteExternalAccount {
            ...HighnoteExternalAccountFragment
          }
        }
        ... on RecurringAchTransferResponse {
          id
          status
          transferAmount {
            # ...TransferAmountFragment

            __typename
            ... on ManualTransferAmountResponse {
              renderedAmount {
                amount
                currencyCode
              }
            }
            ... on TransferAmountCalculationResponse {
              calculatedType
            }
          }
          nextScheduledTransferDate
          fromHighnoteExternalAccount {
            ...HighnoteExternalAccountFragment
          }
        }
      }
    }
  }
`)

export const calculatedTypeText = {
  [CalculatedTransferAmountType.MinimumPayment]: 'Minimum Payment',
  [CalculatedTransferAmountType.OutstandingBalance]: 'Current Balance',
  [CalculatedTransferAmountType.OutstandingStatementBalance]: 'Remaining Statement Balance',
} as const satisfies Record<CalculatedTransferAmountType, string>

export function normalizeScheduledTransfer(
  transfer: GetIncomingScheduledTransfersQuery['incomingScheduledTransfers']['page'][number],
) {
  return {
    ...transfer,
    transferAmount: {
      ...transfer.transferAmount,
      text:
        transfer.transferAmount?.__typename === 'ManualTransferAmountResponse'
          ? `-${transfer.transferAmount.renderedAmount.amount}`
          : transfer.transferAmount?.__typename === 'TransferAmountCalculationResponse'
            ? calculatedTypeText[transfer.transferAmount.calculatedType]
            : '',
    },
    incomingScheduledTransferDate:
      transfer.__typename === 'OneTimeAchTransferResponse' && transfer.scheduledTransferDate
        ? transfer.scheduledTransferDate
        : transfer.__typename === 'RecurringAchTransferResponse' &&
            transfer.nextScheduledTransferDate
          ? transfer.nextScheduledTransferDate
          : undefined,
  } as const
}

export function useLegacyScheduledTransfers(
  options?: QueryHookOptions<
    GetIncomingScheduledTransfersQuery,
    GetIncomingScheduledTransfersQueryVariables
  >,
) {
  const onError = useLogError()

  const query = useLoggedInQuery(GET_INCOMING_SCHEDULED_TRANSFERS, {
    ...options,
    // if you don't include this, `TransferAmountFragment` won't work :jackie-chan-confused:
    // https://github.com/dotansimha/graphql-code-generator/issues/9783#issuecomment-1853170540
    // fetchPolicy: 'no-cache',
    // but we need really do want cache-and-network
    fetchPolicy: 'cache-and-network',
    onError(...args) {
      onError(...args)
      options?.onError?.(...args)
    },
  })

  const { data: unnormalizedData, loading, refetch } = query

  return useMemo(() => {
    const data = unnormalizedData?.incomingScheduledTransfers.page
      .map(normalizeScheduledTransfer)
      // highnote allegedly only allows up to 3 scheduled transfers at a time
      // https://github.com/Acre-Rewards/acre-rewards-api/blob/58e79f2c459a352a3db1b3f4ab48f488d6f2f29f/src/credit-card/credit-card.service.ts#L794
      .toSorted((a, b) =>
        a.incomingScheduledTransferDate && b.incomingScheduledTransferDate
          ? sortByTimeDescending(a.incomingScheduledTransferDate, b.incomingScheduledTransferDate)
          : 0,
      )

    return { data, loading, refetch } as const
  }, [unnormalizedData, loading, refetch])
}

export type UseLegacyScheduledTransfers = typeof useLegacyScheduledTransfers

export type LegacyScheduledTransferActivity = NonNullable<
  ReturnType<UseLegacyScheduledTransfers>['data']
>[number]
